import { Fragment, type ReactNode, useState } from 'react';
import { Dialog, DialogTrigger, Popover } from 'react-aria-components';
import cx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  IconArrowLeftOutlined,
  IconDotsVerticalOutlined,
  IconFiltersOutlined,
  IconPlusOutlined,
} from '@repo/monochrome-icons';
import { Button } from '../../button';
import { Input } from '../../form-elements/input';
import { Spinner } from '../../spinner';
import { Tooltip } from '../../tooltip';
import styles from './new-widget.module.css';

export const WIDGET_RENAME_MAX_LENGTH = 75;

interface NewWidgetProps {
  title: string;
  subtitle?: ReactNode;
  children?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onRemove?: () => void;
  onRename?: (name: string) => void;
  onDuplicate?: () => void;
  className?: string;
  contentPadding?: string | number;
  filterSlot?: ReactNode;
  headerActions?: { id: string; slot: ReactNode }[];
  hideOverflow?: boolean;
}

interface WidgetMenuProps {
  title: string;
  onRemove?: () => void;
  onRename?: (name: string) => void;
  onDuplicate?: () => void;
}

function WidgetMenu({ title, onRemove, onRename, onDuplicate }: WidgetMenuProps): ReactNode {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(title);

  const menuButtonLabel = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.menu-button.label',
  });
  const renameMenuItemLabel = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.rename-menu-item.label',
  });
  const renamePanelTitle = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.rename-panel-title.label',
  });
  const duplicateMenuItemLabel = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.duplicate-menu-item.label',
  });
  const removeLabel = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.remove-menu-item.label',
  });
  const backButtonLabel = formatMessage({
    id: 'a11y-buttons-close-aria-label',
  });

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    if (!open) setIsRenaming(false);
  };

  const handleRenameSubmit = (): void => {
    const trimmed = draftName.trim();
    if (trimmed) onRename?.(trimmed);
    handleOpenChange(false);
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip label={menuButtonLabel} placement="top">
        <Button aria-label={menuButtonLabel} iconOnly size="small" variant="tertiary">
          <IconDotsVerticalOutlined height={16} width={16} />
        </Button>
      </Tooltip>
      <Popover className={styles.menuPopover} offset={8} placement="bottom start" shouldFlip>
        <Dialog aria-label={menuButtonLabel} className={styles.menuDialog}>
          {isRenaming ? (
            <>
              <div className={styles.renameHeader}>
                <Button
                  aria-label={backButtonLabel}
                  iconOnly
                  onPress={() => {
                    setIsRenaming(false);
                  }}
                  size="xsmall"
                  variant="tertiary"
                >
                  <IconArrowLeftOutlined height={16} width={16} />
                </Button>
                <span className={cx('body-standard', styles.renameHeaderTitle)}>
                  {renamePanelTitle}
                </span>
              </div>
              <div className={styles.renameInputSection}>
                <Input
                  className={styles.renameInput}
                  maxLength={WIDGET_RENAME_MAX_LENGTH}
                  onChange={({ target }: { target: HTMLInputElement }) => {
                    const value = target.value;
                    setDraftName(value);
                    if (value.trim()) onRename?.(value);
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') handleRenameSubmit();
                  }}
                  type="text"
                  value={draftName}
                />
              </div>
            </>
          ) : (
            <div role="menu">
              {onDuplicate ? (
                <Button
                  className={styles.menuItem}
                  onPress={() => {
                    onDuplicate();
                    handleOpenChange(false);
                  }}
                  stretch
                  variant="tertiary"
                >
                  {duplicateMenuItemLabel}
                </Button>
              ) : null}
              {onRename ? (
                <Button
                  className={styles.menuItem}
                  onPress={() => {
                    setDraftName(title);
                    setIsRenaming(true);
                  }}
                  stretch
                  variant="tertiary"
                >
                  {renameMenuItemLabel}
                </Button>
              ) : null}
              {onRemove ? (
                <Button
                  className={cx(styles.menuItem, styles.menuItemDestructive)}
                  onPress={() => {
                    onRemove();
                    handleOpenChange(false);
                  }}
                  stretch
                  variant="tertiary"
                >
                  {removeLabel}
                </Button>
              ) : null}
            </div>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

interface FiltersProps {
  children: ReactNode;
  hasActiveFilter?: boolean;
  hideLabel?: boolean;
}

function Filters({
  children,
  hasActiveFilter = false,
  hideLabel = false,
}: FiltersProps): ReactNode {
  const { formatMessage } = useIntl();

  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;

  const label = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.filter-button.label',
  });

  const filterByLabel = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.filter-panel.label',
  });

  return (
    <DialogTrigger>
      <Tooltip label={label} placement="top">
        <Button
          aria-label={label}
          className={styles.filterButton}
          iconOnly
          size="small"
          variant="tertiary"
        >
          <IconFiltersOutlined height={16} width={16} />
          {hasActiveFilter ? (
            <span
              aria-hidden="true"
              className={styles.filterIndicator}
              data-testid="active-filter-dot"
            />
          ) : null}
        </Button>
      </Tooltip>
      <Popover
        UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
        className={styles.filterPopover}
        offset={8}
        placement="bottom start"
        shouldFlip
      >
        <Dialog aria-label={filterByLabel} className={styles.filterDialog}>
          {hideLabel ? null : (
            <span className={cx('caption', styles.filterByLabel)}>{filterByLabel}</span>
          )}
          {children}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

interface FiltersContentProps {
  children: ReactNode;
}

function FiltersContent({ children }: FiltersContentProps): ReactNode {
  return <div className={styles.filterContent}>{children}</div>;
}

function FilterSeparator(): ReactNode {
  return <hr className={styles.filterSeparator} />;
}

interface AdvancedFiltersButtonProps {
  onPress?: () => void;
  label?: string;
  icon?: ReactNode;
}

function AdvancedFiltersButton({
  onPress,
  label,
  icon = <IconPlusOutlined className={styles.iconPlus} />,
}: AdvancedFiltersButtonProps): ReactNode {
  const { formatMessage } = useIntl();

  return (
    <Button className={styles.advancedFiltersButton} onPress={onPress} stretch variant="secondary">
      <div className={styles.buttonContentWrapper}>
        {icon}
        <span className={styles.buttonContentText}>
          {label
            ? label
            : formatMessage({
                id: 'design-system-kit.modular-home.new-widget.advanced-filters-button.label',
              })}
        </span>
      </div>
    </Button>
  );
}

interface HeaderActionProps {
  children: ReactNode;
  ariaLabel: string;
  onPress: () => void;
}

function HeaderAction({ children, ariaLabel, onPress }: HeaderActionProps): ReactNode {
  return (
    <Tooltip label={ariaLabel} placement="top">
      <Button aria-label={ariaLabel} iconOnly onPress={onPress} size="small" variant="tertiary">
        {children}
      </Button>
    </Tooltip>
  );
}

function NewWidgetComponent({
  title,
  subtitle,
  children,
  isLoading,
  isError,
  onRetry,
  onRemove,
  onRename,
  onDuplicate,
  className,
  contentPadding = '16px',
  filterSlot,
  headerActions,
  hideOverflow = true,
}: NewWidgetProps): ReactNode {
  const renderContent = (): ReactNode => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Spinner size="medium" />
        </div>
      );
    }

    if (isError) {
      return (
        <div className={styles.errorContainer}>
          <p className="body-standard">
            <FormattedMessage id="design-system-kit.modular-home.new-widget.loading-failed-title.label" />
          </p>
          {onRetry ? (
            <Button onPress={onRetry} size="small" variant="tertiary">
              <FormattedMessage id="design-system-kit.modular-home.new-widget.try-again-cta.label" />
            </Button>
          ) : null}
        </div>
      );
    }

    return children;
  };

  const hasMenu = Boolean(onRemove ?? onRename ?? onDuplicate);
  const activeSlots = headerActions?.filter(item => Boolean(item.slot)) ?? [];
  const hasHeaderActions = Boolean(activeSlots.length || filterSlot || hasMenu);

  return (
    <div className={cx(styles.wrapper, hideOverflow && styles.hideOverflow, className)}>
      <div className={styles.header}>
        <h4 className={cx('body-standard', styles.title)}>{title}</h4>
        {hasHeaderActions ? (
          <div className={styles.headerActions}>
            {activeSlots.map(({ id, slot }) => (
              <Fragment key={id}>{slot}</Fragment>
            ))}
            {filterSlot}
            {hasMenu ? (
              <WidgetMenu
                onDuplicate={onDuplicate}
                onRemove={onRemove}
                onRename={onRename}
                title={title}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      {subtitle ? (
        <div className={styles.subtitleContainer}>
          <p className="caption">{subtitle}</p>
        </div>
      ) : null}
      <div className={styles.content} style={{ padding: contentPadding }}>
        {renderContent()}
      </div>
    </div>
  );
}

export const NewWidget = Object.assign(NewWidgetComponent, {
  Filters,
  FiltersContent,
  FilterSeparator,
  AdvancedFiltersButton,
  HeaderAction,
});
